<template>
  <div
    :class="classObj"
    :style="{ '--current-color': theme }"
    class="app-wrapper"
  >
    <div
      v-if="device === 'mobile' && sidebar.opened"
      class="drawer-bg"
      @click="handleClickOutside"
    />
    <sidebar v-if="!sidebar.hide" class="sidebar-container"/>
    <div
      :class="{ hasTagsView: needTagsView, sidebarHide: sidebar.hide }"
      class="main-container"
    >
      <div :class="{ 'fixed-header': fixedHeader }">
        <navbar/>
        <tags-view v-if="needTagsView"/>
      </div>
      <app-main/>
      <right-panel>
        <settings/>
      </right-panel>
    </div>
  </div>
</template>

<script>
import RightPanel from "@/components/RightPanel";
import {AppMain, Navbar, Settings, Sidebar, TagsView} from "./components";
import ResizeMixin from "./mixin/ResizeHandler";
import {mapState} from "vuex";
import variables from "@/assets/styles/variables.scss";
import {pendingOrderReminder} from "@/api/bus/appointment";

// 全局每 30秒 检查一次
const APPOINTMENT_REMINDER_INTERVAL = 20 * 1000;

export default {
  name: "Layout",

  components: {
    AppMain,
    Navbar,
    RightPanel,
    Settings,
    Sidebar,
    TagsView,
  },

  mixins: [ResizeMixin],
  data() {
    return {
      appointmentReminderTimer: null,
      appointmentReminderLoading: false,
      appointmentReminderAudioContext: null,
      appointmentReminderNotifications: [],
      latestPendingOrderKey: null,
    };
  },
  computed: {
    ...mapState({
      theme: (state) => state.settings.theme,
      sideTheme: (state) => state.settings.sideTheme,
      sidebar: (state) => state.app.sidebar,
      device: (state) => state.app.device,
      needTagsView: (state) => state.settings.tagsView,
      fixedHeader: (state) => state.settings.fixedHeader,
      permissions: (state) => state.user.permissions,
    }),
    canViewAppointments() {
      return this.permissions.includes("*:*:*")
        || this.permissions.includes("bus:appointment:list");
    },
    classObj() {
      return {
        hideSidebar: !this.sidebar.opened,
        openSidebar: this.sidebar.opened,
        withoutAnimation: this.sidebar.withoutAnimation,
        mobile: this.device === "mobile",
      };
    },
    variables() {
      return variables;
    },
  },
  watch: {
    canViewAppointments(canView) {
      if (canView) {
        this.startAppointmentReminder();
      } else {
        this.stopAppointmentReminder();
      }
    },
  },
  mounted() {
    this.startAppointmentReminder();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    document.addEventListener("pointerdown", this.unlockAppointmentReminderSound, {once: true});
    document.addEventListener("keydown", this.unlockAppointmentReminderSound, {once: true});
  },
  beforeDestroy() {
    this.stopAppointmentReminder();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    document.removeEventListener("pointerdown", this.unlockAppointmentReminderSound);
    document.removeEventListener("keydown", this.unlockAppointmentReminderSound);
    if (this.appointmentReminderAudioContext) {
      this.appointmentReminderAudioContext.close();
      this.appointmentReminderAudioContext = null;
    }
    this.appointmentReminderNotifications.splice(0).forEach((notification) => {
      notification.close();
    });
  },
  methods: {
    handleClickOutside() {
      this.$store.dispatch("app/closeSideBar", {withoutAnimation: false});
    },
    startAppointmentReminder() {
      if (!this.canViewAppointments || this.appointmentReminderTimer) return;
      this.checkPendingAppointments();
      this.appointmentReminderTimer = window.setInterval(
        this.checkPendingAppointments,
        APPOINTMENT_REMINDER_INTERVAL
      );
    },
    stopAppointmentReminder() {
      if (!this.appointmentReminderTimer) return;
      window.clearInterval(this.appointmentReminderTimer);
      this.appointmentReminderTimer = null;
    },
    handleVisibilityChange() {
      if (!document.hidden) {
        this.checkPendingAppointments();
      }
    },
    getAppointmentReminderAudioContext() {
      if (this.appointmentReminderAudioContext) {
        return this.appointmentReminderAudioContext;
      }
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;
      this.appointmentReminderAudioContext = new AudioContextClass();
      return this.appointmentReminderAudioContext;
    },
    unlockAppointmentReminderSound() {
      const audioContext = this.getAppointmentReminderAudioContext();
      if (audioContext && audioContext.state === "suspended") {
        audioContext.resume().catch(() => {});
      }
    },
    playAppointmentReminderSound() {
      const audioContext = this.getAppointmentReminderAudioContext();
      if (!audioContext || audioContext.state !== "running") return;
      const now = audioContext.currentTime;
      [
        {frequency: 880, start: 0, duration: 0.14},
        {frequency: 1174.66, start: 0.17, duration: 0.22},
      ].forEach((tone) => {
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const startTime = now + tone.start;
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(tone.frequency, startTime);
        gain.gain.setValueAtTime(0.0001, startTime);
        gain.gain.exponentialRampToValueAtTime(0.12, startTime + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, startTime + tone.duration);
        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start(startTime);
        oscillator.stop(startTime + tone.duration);
      });
    },
    async checkPendingAppointments() {
      if (!this.canViewAppointments || document.hidden || this.appointmentReminderLoading) return;
      this.appointmentReminderLoading = true;
      try {
        const response = await pendingOrderReminder();
        const reminder = response.data || {};
        const latestKey = reminder.latestOrderId
          ? `${reminder.createTime || ""}_${reminder.latestOrderId}`
          : "";

        if (this.latestPendingOrderKey === null) {
          this.latestPendingOrderKey = latestKey;
          if (Number(reminder.pendingCount) > 0) {
            this.showAppointmentReminder(reminder, false);
          }
          return;
        }

        if (latestKey && latestKey > this.latestPendingOrderKey) {
          this.latestPendingOrderKey = latestKey;
          this.showAppointmentReminder(reminder, true);
          window.dispatchEvent(new Event("bus-order-created"));
        }
      } catch (error) {
        // 请求层统一处理异常，轮询保持静默并等待下次检查。
      } finally {
        this.appointmentReminderLoading = false;
      }
    },
    showAppointmentReminder(reminder, isNewOrder) {
      const count = Number(reminder.pendingCount) || 0;
      const trip = [reminder.routeName, reminder.stationName].filter(Boolean).join(" / ");
      const details = isNewOrder
        ? `${reminder.userName || "有用户"}提交了新的预约${trip ? `：${trip}` : ""}。当前共${count}条待确认。`
        : `当前有${count}条预约等待确认，请及时处理。`;
      while (this.appointmentReminderNotifications.length >= 3) {
        const oldestNotification = this.appointmentReminderNotifications.shift();
        oldestNotification.close();
      }
      this.playAppointmentReminderSound();
      const notification = this.$notify({
        title: isNewOrder ? "收到新的公交预约" : "有待处理的公交预约",
        message: `${details} 点击查看预约管理。`,
        type: "warning",
        duration: isNewOrder ? 0 : 10000,
        position: "top-right",
        onClick: () => {
          notification.close();
          this.openAppointmentPage();
        },
        onClose: () => {
          this.appointmentReminderNotifications = this.appointmentReminderNotifications
            .filter((item) => item !== notification);
        },
      });
      this.appointmentReminderNotifications.push(notification);
    },
    openAppointmentPage() {
      if (this.$route.path === "/project/appointment") {
        window.dispatchEvent(new Event("bus-order-created"));
        return;
      }
      this.$router.push("/project/appointment").catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/mixin.scss";
@import "~@/assets/styles/variables.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - #{$base-sidebar-width});
  transition: width 0.28s;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px);
}

.sidebarHide .fixed-header {
  width: 100%;
}

.mobile .fixed-header {
  width: 100%;
}
</style>
